import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, TrendingUp, Users, UserCircle, Database, Bell, LogOut, Menu, X, PanelLeftClose, PanelLeft, Dumbbell, CheckCircle, BarChart3, Activity, ClipboardList, Send, GitBranch, HelpCircle, ArrowRightLeft, LogIn, Calendar, ClipboardCheck, RefreshCw, Headphones, Target, Package, Clock, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import { useAttendanceGate } from '@/context/AttendanceContext';
import { useWtfSyncStatus } from '@/hooks/useWtfLiveData';

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [showWtfPanel, setShowWtfPanel] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const wtfPanelRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isCheckedIn, openCheckOutModal, nudgeCheckIn, setShowCheckInModal, activeShift, showCheckInModal, canBreakShift } = useAttendanceGate();
  const isAdmin = user.role === 'admin';
  const isFieldStaff = ['team_leader', 'fitness_manager'].includes(user.role);
  const { feeds: wtfFeeds } = useWtfSyncStatus(isAdmin ? 300000 : null);

  // Compute overall WTF sync health for header badge
  const wtfOverallHealth = useMemo(() => {
    if (!wtfFeeds || wtfFeeds.length === 0) return null;
    const now = Date.now();
    const feedStatuses = wtfFeeds.map(f => {
      if (!f.synced_at) return 'offline';
      const age = (now - new Date(f.synced_at).getTime()) / 1000;
      if (age > 600) return 'stale';
      if (age > 300) return 'warning';
      return 'healthy';
    });
    if (feedStatuses.includes('offline') || feedStatuses.includes('stale')) return 'stale';
    if (feedStatuses.includes('warning')) return 'warning';
    return 'healthy';
  }, [wtfFeeds]);

  const wtfHealthyCount = useMemo(() => {
    if (!wtfFeeds || wtfFeeds.length === 0) return 0;
    const now = Date.now();
    return wtfFeeds.filter(f => {
      if (!f.synced_at) return false;
      return (now - new Date(f.synced_at).getTime()) / 1000 <= 300;
    }).length;
  }, [wtfFeeds]);

  const [pendingRosterCount, setPendingRosterCount] = useState(0);
  const [blockingCommitmentCount, setBlockingCommitmentCount] = useState(0);
  const [pendingVerificationCount, setPendingVerificationCount] = useState(0);
  const [pendingRegularizationCount, setPendingRegularizationCount] = useState(0);

  // Fetch alert count for badge
  const fetchAlertCount = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.get(`/alerts?date=${today}`);
      const criticalAlerts = response.data.filter(a => a.severity === '🔴' || a.severity === 'critical');
      setAlertCount(criticalAlerts.length);
    } catch (error) {
      // Silently fail - badge just won't show
    }
  };

  // Fetch pending approvals counts for sidebar badges
  const fetchPendingCounts = async () => {
    try {
      // Admin: pending roster approvals
      if (isAdmin) {
        const rosterRes = await axiosInstance.get('/rosters/pending');
        setPendingRosterCount(Array.isArray(rosterRes.data) ? rosterRes.data.length : 0);
      }
      // TL: blocking commitments count
      if (user.role === 'team_leader') {
        const commitRes = await axiosInstance.get('/pulse-entries/blocking-commitments');
        setBlockingCommitmentCount(commitRes.data?.count || 0);
      }
      // Manager/Admin: pending verifications (entries where TL verified but manager hasn't)
      if (isAdmin || user.role === 'manager') {
        const verifRes = await axiosInstance.get('/pulse-entries/pending-verification');
        setPendingVerificationCount(Array.isArray(verifRes.data) ? verifRes.data.length : 0);
      }
      // Manager/Admin: pending regularization requests awaiting approval
      if (isAdmin || user.role === 'manager') {
        const regRes = await axiosInstance.get('/attendance/daily/regularization/count');
        setPendingRegularizationCount(regRes.data?.count || 0);
      }
    } catch (error) {
      // Silently fail - badges just won't show
    }
  };

  useEffect(() => {
    fetchAlertCount();
    fetchPendingCounts();
    const alertInterval = setInterval(fetchAlertCount, 300000);
    const pendingInterval = setInterval(fetchPendingCounts, 300000);
    return () => {
      clearInterval(alertInterval);
      clearInterval(pendingInterval);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280) {
        setSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Nudge check-in on page navigation
  useEffect(() => {
    nudgeCheckIn?.();
  }, [location.pathname]);

  // Close WTF panel on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wtfPanelRef.current && !wtfPanelRef.current.contains(e.target)) {
        setShowWtfPanel(false);
      }
    };
    if (showWtfPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showWtfPanel]);

  // Manual sync all feeds
  const handleSyncAll = async () => {
    if (!isAdmin) return;
    setSyncingAll(true);
    try {
      await axiosInstance.post('/wtf/sync/trigger-all');
    } catch (e) {
      // silently fail
    } finally {
      setTimeout(() => setSyncingAll(false), 3000);
    }
  };

  // Format synced_at to IST relative time
  const formatSyncAge = (syncedAt) => {
    if (!syncedAt) return 'Never';
    const age = (Date.now() - new Date(syncedAt).getTime()) / 1000;
    if (age < 60) return `${Math.round(age)}s ago`;
    if (age < 3600) return `${Math.round(age / 60)}m ago`;
    if (age < 86400) return `${Math.round(age / 3600)}h ago`;
    return `${Math.round(age / 86400)}d ago`;
  };

  const getFeedHealth = (feed) => {
    if (!feed.synced_at) return 'offline';
    const age = (Date.now() - new Date(feed.synced_at).getTime()) / 1000;
    if (age > 600) return 'stale';
    if (age > 300) return 'warning';
    return 'healthy';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const navItems = [
    { path: '/command-center', label: 'Command Center', icon: LayoutDashboard, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, roles: ['admin', 'manager', 'team_leader', 'inside_sales_manager', 'fitness_manager', 'front_desk'] },
    { path: '/daily-targets', label: 'Daily Targets', icon: Target, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/pulse-entry', label: 'Pulse Entry', icon: FileText, roles: ['admin', 'team_leader'] },
    { path: '/pulse-operations', label: 'Pulse Operations', icon: Activity, roles: ['admin', 'manager', 'team_leader', 'front_desk'] },
    { path: '/active-gyms', label: 'Active Gyms', icon: Dumbbell, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/gym-performance', label: 'Gym Performance', icon: BarChart3, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/commitment-dashboard', label: 'Commitments', icon: CheckCircle, roles: ['admin', 'manager', 'team_leader'], badge: user.role === 'team_leader' ? blockingCommitmentCount : 0 },
    { path: '/movement-analytics', label: 'Movement Analytics', icon: ArrowRightLeft, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/historical', label: 'Historical', icon: TrendingUp, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/sales-funnel', label: 'Sales Funnel', icon: BarChart3, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/sales-intelligence', label: 'Sales Intelligence', icon: Activity, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/inside-sales', label: 'Inside Sales', icon: Headphones, roles: ['admin', 'inside_sales_manager', 'manager'] },
    { path: '/closure-expected', label: 'Closure Verification', icon: Target, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/verification', label: 'Verification', icon: CheckCircle, roles: ['admin', 'manager', 'team_leader'], badge: pendingVerificationCount },
    { path: '/manager-view', label: 'Manager View', icon: Users, roles: ['admin', 'manager'] },
    { path: '/tl-view', label: 'TL View', icon: UserCircle, roles: ['admin', 'manager', 'team_leader'] },
    { path: '/source-view', label: 'Source View', icon: Database, roles: ['admin', 'manager'] },
    { path: '/alerts', label: 'Alerts', icon: Bell, roles: ['admin', 'manager', 'team_leader'], badge: alertCount },
    // Hidden: roster system (optional feature, accessible via direct URL)
    // { path: '/roster-management', label: 'Roster Management', icon: Calendar, roles: ['admin', 'manager', 'team_leader'], badge: isAdmin ? pendingRosterCount : 0 },
    { path: '/my-roster', label: 'Mark Attendance', icon: Clock, roles: ['manager', 'team_leader', 'fitness_manager'] },
    // Hidden: old verification system (replaced by inline justification flow)
    // { path: '/attendance-verifications', label: 'Attendance Verifications', icon: ClipboardCheck, roles: ['admin'] },
    { path: '/attendance-dashboard', label: 'Attendance Report', icon: Users, roles: ['admin', 'manager', 'team_leader', 'fitness_manager'], badge: pendingRegularizationCount },
    { path: '/fm', label: 'FM Operations', icon: Dumbbell, roles: ['admin', 'manager'] },
    { path: '/supplements', label: 'Supplement Tracker', icon: Package, roles: ['admin', 'manager', 'team_leader', 'ops_team'] },
    { path: '/master-config', label: 'Master Config', icon: Database, roles: ['admin'] },
    { path: '/activity-logs', label: 'Activity Logs', icon: ClipboardList, roles: ['admin'] },
    { path: '/notifications', label: 'Notifications', icon: Send, roles: ['admin'] },
    { path: '/rules', label: 'Rules', icon: GitBranch, roles: ['admin'] },
    { path: '/help', label: 'Help', icon: HelpCircle, roles: ['admin', 'manager', 'team_leader'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-testid="layout-header">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="sidebar-toggle"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
              </button>
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1F4E79] to-[#0F2942] flex items-center justify-center">
                  <Dumbbell size={18} className="text-white" />
                </div>
                <div>
                  <h1 className="font-display text-xl font-bold tracking-tight text-[#1F4E79]">Sales Tracker</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {wtfOverallHealth && (
                <div className="relative hidden sm:block" ref={wtfPanelRef}>
                  <button
                    onClick={() => setShowWtfPanel(!showWtfPanel)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-xs cursor-pointer hover:bg-gray-200 transition-colors"
                    data-testid="wtf-live-badge"
                    title="Click to view feed details"
                  >
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      wtfOverallHealth === 'healthy' ? 'bg-green-500 animate-pulse' :
                      wtfOverallHealth === 'warning' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}></span>
                    <span className="text-gray-600 font-medium whitespace-nowrap">WTF Live</span>
                    <span className="text-gray-400 tabular-nums min-w-[2ch] text-right">
                      {wtfHealthyCount}/{wtfFeeds.length}
                    </span>
                  </button>

                  {/* WTF Live Feed Status Panel */}
                  {showWtfPanel && (
                    <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-2xl border border-slate-200 z-[100] overflow-hidden">
                      {/* Panel Header */}
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">WTF Feed Status</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {wtfHealthyCount} of {wtfFeeds.length} feeds healthy
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSyncAll(); }}
                          disabled={syncingAll}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#1F4E79] text-white rounded-lg hover:bg-[#163a5c] disabled:opacity-50 transition-colors"
                        >
                          <RefreshCw size={12} className={syncingAll ? 'animate-spin' : ''} />
                          {syncingAll ? 'Syncing...' : 'Sync All'}
                        </button>
                      </div>

                      {/* Feed List */}
                      <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
                        {wtfFeeds.map((feed) => {
                          const health = getFeedHealth(feed);
                          const dotColor = health === 'healthy' ? 'bg-green-500' :
                                          health === 'warning' ? 'bg-yellow-500' :
                                          health === 'stale' ? 'bg-red-500' : 'bg-gray-400';
                          const bgColor = health === 'healthy' ? '' :
                                          health === 'warning' ? 'bg-yellow-50' :
                                          health === 'stale' ? 'bg-red-50' :
                                          'bg-gray-50';
                          return (
                            <div key={feed.feed} className={`flex items-center justify-between px-4 py-2.5 ${bgColor}`}>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`}></span>
                                <span className="text-sm text-slate-700 font-medium truncate">
                                  {feed.feed.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0 text-right">
                                <span className="text-xs text-slate-400 tabular-nums min-w-[3.5rem]">
                                  {feed.count > 0 ? `${feed.count} rec` : '-'}
                                </span>
                                <span className={`text-xs tabular-nums min-w-[4rem] ${
                                  health === 'healthy' ? 'text-green-600' :
                                  health === 'warning' ? 'text-yellow-600' :
                                  health === 'stale' ? 'text-red-600' :
                                  'text-gray-400'
                                }`}>
                                  {formatSyncAge(feed.synced_at)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Panel Footer */}
                      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-400">
                        Auto-refreshes every 5m
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-slate-900">{user.name}</div>
                <div className="text-xs text-slate-500 capitalize">{user.role?.replace('_', ' ')}</div>
              </div>
              {isCheckedIn ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openCheckOutModal}
                  data-testid="checkout-button"
                  data-track="attendance-check-out"
                  className="flex items-center gap-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 text-green-700"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Check Out</span>
                </Button>
              ) : ((isFieldStaff || user?.role === 'manager') && !isAdmin) || canBreakShift ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCheckInModal(true)}
                  disabled={showCheckInModal}
                  data-testid="checkin-button"
                  data-track="attendance-check-in"
                  className={`flex items-center gap-2 ${canBreakShift
                    ? 'border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 text-purple-700'
                    : 'border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 text-blue-700 animate-pulse'
                  }`}
                >
                  <LogIn size={16} />
                  <span className="hidden sm:inline">{canBreakShift ? 'Break Shift' : 'Check In'}</span>
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
                data-track="auth-logout"
                className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop - Dark Theme */}
        <aside
          className={`
            hidden lg:flex lg:flex-col sticky top-[57px] h-[calc(100vh-57px)] sidebar-dark
            transition-all duration-300
            ${sidebarCollapsed ? 'w-16' : 'w-56'}
          `}
          data-testid="sidebar"
        >
          <nav className="flex-1 overflow-y-auto p-2 space-y-1 mt-2 scrollbar-thin">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path}`}
                  data-track={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={`
                    nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all relative
                    ${isActive ? 'active' : ''}
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  {!sidebarCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
                  {item.badge > 0 && (
                    <span className={`
                      absolute bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center
                      ${sidebarCollapsed ? 'top-0 right-0' : 'right-2'}
                    `}>
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          {!sidebarCollapsed && (
            <div className="flex-shrink-0 p-4">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="text-xs text-slate-400">v2.0</div>
                <div className="text-xs text-slate-500 mt-1">Performance Edition</div>
              </div>
            </div>
          )}
        </aside>

        {/* Sidebar - Mobile */}
        <aside
          className={`
            lg:hidden fixed top-[57px] left-0 h-[calc(100vh-57px)] sidebar-dark
            transition-transform duration-300 z-40 w-64 overflow-y-auto
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <nav className="p-3 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    nav-item flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                    ${isActive ? 'active' : ''}
                  `}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <main
          className="flex-1 p-4 lg:p-6 min-w-0"
          data-testid="main-content"
          onClick={() => {
            if (!sidebarCollapsed) setSidebarCollapsed(true);
            if (mobileMenuOpen) setMobileMenuOpen(false);
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
